/**
 * ---------------------------------------------------------------------------------------------------------------
 * GROWTHBOOK — BUSINESS EVENTS
 * ---------------------------------------------------------------------------------------------------------------
 *
 * Event NAMES and PAYLOAD SHAPES follow the GrowthBook Event Tracking PRD (Epic 2). Per-test SCOPE (which
 * collection needs which event) follows the team's own event list, which is broader than the PRD's Epic 7 table
 * in two places: scroll_depth also covers Tests 2 and 3, and grid_breaker_view_item exists as a named event for
 * Test 5 rather than being folded into PLP → PDP View Item. One file, five collection pages, each its own test:
 *
 *   shoulder-bags      card_rewamp_atc_swatches                  Variant Click, Wishlist Click
 *   tote-bags          product_tags_swatches                     Variant Click, Wishlist Click, Scroll Depth
 *   women-laptop-bags  swipe_quickview_swatches                  Variant Click, Image Swipe, Quick View Open,
 *                                                                Scroll Depth
 *   bestsellers        swatches_swipe_quickview_atc_producttags  Variant Click, Image Swipe, Quick View Open
 *   work-essentials    Grid_breaker                              Image Swipe, Component Visibility,
 *                                                                Grid Breaker View Item, Scroll Depth
 *
 * EVENT NAMES ARE THE GROWTHBOOK METRIC NAMES, NOT THE PRD'S (changed 2026-09-03). Four of them differ from the
 * PRD's Epic 2 naming because the metrics in GrowthBook are Fact Table metrics whose entire definition is a SQL
 * row filter — `WHERE (event_name = '…')` — over a shared "GA4 Events" fact table that does not expose GA4 event
 * params as columns. Matching the storefront to those names was the change that did not require surgery on a
 * fact table every other metric depends on. The mapping, for anyone reading the PRD alongside this file:
 *
 *   PRD name              emitted as
 *   image_swipe           image_swipe_count             (context 'card')
 *   image_swipe           quick_view_image_swipe_count  (context 'quick_view')
 *   quick_view_open       quick_view_click
 *   component_visibility  component_viewability_rate
 *
 * variant_click, wishlist_click, scroll_depth and grid_breaker_view_item already matched and are unchanged.
 *
 * Add to Cart, PLP→PDP View Item, Purchase, Non-Bounce Rate and Avg. Time Spent are NOT here (PRD Epic 3) — they
 * come from GA4's existing events and are configured as metrics in GrowthBook directly.
 *
 * NO EXPERIMENT IDENTIFIERS. These payloads carry no experimentId or variationId, deliberately — that is the
 * "common implementation mistake" the PRD opens with. GrowthBook joins exposure to conversion in BigQuery on
 * anonymous_id; experiment_viewed (with those identifiers) is the Auto SDK's own automatic event, not this file's
 * concern — see snippets/experiments.liquid.
 *
 * DELEGATED, NOT PER-ELEMENT. One delegated listener per event family covers every card on the page, including
 * ones a colour swap or a quick-view fetch adds after load.
 *
 * CAPTURE PHASE, for the swatch/wishlist listener: custom.js and product-card-ab.js both intercept those clicks
 * (preventDefault, and stopPropagation in places) to swap a card or the drawer panel in place. A bubble-phase
 * listener can be cut off before it ever runs; a capture-phase one on the document always sees it.
 */

(function () {
  'use strict';

  var config = window.__growthbook || {};

  /** Every custom event carries product_id and collection (PRD 2.6). The collection handle is resolved in
   *  Liquid by snippets/experiments.liquid and published onto window.__growthbook before this file runs. */
  function send(name, params) {
    // Ad blockers and consent banners can leave gtag undefined; a lost business event must never throw into the
    // click handler and break the swatch, the heart, or navigation.
    if (typeof window.gtag !== 'function') return;

    params.collection = config.collection || '';

    // Read at send time, not cached at load: snippets/experiments.liquid can flip this after a queued
    // growthbookdata event, and a click a moment later must report the arm the shopper is actually looking at,
    // not whatever it was when this file first ran. 'unassigned' means the page never resolved an arm at all
    // (e.g. GrowthBook returned unknownFeature) — distinct from a real control/variant value.
    params.arm = document.documentElement.getAttribute('data-arm') || 'unassigned';

    // transport_type: 'beacon' survives a click that navigates the page before the request would otherwise land.
    params.transport_type = 'beacon';

    window.gtag('event', name, params);
  }

  /** 'quick_view' or 'card' — the drawer is the only other surface these controls appear on. */
  function contextOf(element) {
    return element.closest('[data-quick-view-target]') ? 'quick_view' : 'card';
  }

  /** 'grid-breaker' or 'card' — PRD 2.2's source param for quick_view_open. */
  function sourceOf(element) {
    return element.closest('.product-card--breaker') ? 'grid-breaker' : 'card';
  }

  /**
   * The numeric Shopify product id of the card (or panel) the shopper is acting on.
   *
   * The card already publishes it as `product-id`, and .product-card_main as `data-pid`; the quick-view panel uses
   * `data-product-id`. Nothing new has to be rendered for this.
   */
  function productIdOf(element) {
    var card = element.closest('.product-card');
    if (card && card.getAttribute('product-id')) return card.getAttribute('product-id');

    var panel = element.closest('.quick-view');
    if (panel && panel.getAttribute('data-product-id')) return panel.getAttribute('data-product-id');

    var wrapper = element.closest('.product-card_main');
    if (wrapper && wrapper.getAttribute('data-pid')) return wrapper.getAttribute('data-pid');

    return '';
  }

  function collectionIn(list) {
    return list.indexOf(config.collection) !== -1;
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * variant_click  —  shoulder-bags, tote-bags, women-laptop-bags, bestsellers
   * ---------------------------------------------------------------------------------------------------------------
   * Miraggio models each colour as its own PRODUCT, linked through the custom.product_variants metafield, so a
   * swatch is an <a> to a sibling product rather than a variant control.
   *
   * PRD 2.1 names the payload field `variant_id`. The chosen colour IS a product, not a variant, so what is sent
   * there is that sibling product's id — the closest real match to "which variant was picked" this data model has.
   * ------------------------------------------------------------------------------------------------------------- */

  var VARIANT_CLICK_COLLECTIONS = ['shoulder-bags', 'tote-bags', 'women-laptop-bags', 'bestsellers'];

  function trackVariantClick(swatch) {
    if (!collectionIn(VARIANT_CLICK_COLLECTIONS)) return;

    // The wrapper carries the destination product's id and colour; see snippets/product-card-swatch-name.liquid,
    // which resolves the name from the metafield or the product's Color option.
    var wrap = swatch.closest('.thumbnail-swatch_item-wrap');

    send('variant_click', {
      product_id: productIdOf(swatch),
      variant_id: (wrap && wrap.getAttribute('data-swatch-product-id')) || '',
      // No fallback to the swatch's accessible label on purpose. option-value falls that label back to the HOST
      // card's variant title rather than the linked product's, so on a product with no colour data it named a
      // different colour entirely — a Blue swatch reported "Grape Wine". An empty colour is a gap in the data; a
      // confidently wrong one silently moves clicks onto the wrong colour in every report built on this.
      colour: (wrap && wrap.getAttribute('data-swatch-colour')) || '',
      context: contextOf(swatch)
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * wishlist_click  —  shoulder-bags, tote-bags only
   * ---------------------------------------------------------------------------------------------------------------
   * Not one of the PRD's new Epic 2 events, but referenced by name in its Epic 7 metric mapping (Test 1 & 2
   * secondary metric) — kept as already implemented.
   *
   * Two different buttons produce it:
   *   .swym-wishlist-collections-v2   Swym's own heart, injected into every card after load
   *   [data-quick-view-wishlist]      the drawer's heart, which the theme renders and wires itself
   *
   * `action` separates saves from un-saves, because a toggle counted as one event would make a shopper who saved
   * and immediately removed look identical to one who saved. aria-pressed is read BEFORE the handlers run — in the
   * capture phase it still holds the pre-click state, so the value it reports is the action being taken.
   * ------------------------------------------------------------------------------------------------------------- */

  var WISHLIST_CLICK_COLLECTIONS = ['shoulder-bags', 'tote-bags'];

  function trackWishlistClick(button) {
    if (!collectionIn(WISHLIST_CLICK_COLLECTIONS)) return;

    var saved = button.getAttribute('aria-pressed') === 'true' || button.classList.contains('is-saved');

    send('wishlist_click', {
      // Swym's own data-product-id is the product URL, not an id, so productIdOf is used for both buttons and the
      // drawer's data-empi (the Shopify product id it hands Swym) is the fallback.
      product_id: productIdOf(button) || button.getAttribute('data-empi') || '',
      action: saved ? 'remove' : 'add',
      context: contextOf(button)
    });
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var swatch = target.closest('.thumbnail-swatch');
      if (swatch) {
        trackVariantClick(swatch);
        return;
      }

      var heart = target.closest('.swym-wishlist-collections-v2, [data-quick-view-wishlist]');
      if (heart) trackWishlistClick(heart);
    },
    true
  );

  /* ---------------------------------------------------------------------------------------------------------------
   * scroll_depth  —  tote-bags, women-laptop-bags, work-essentials
   * ---------------------------------------------------------------------------------------------------------------
   * PRD 3.2 permits scroll_depth as a new event only where granular (10/20/30%) thresholds are actually required;
   * the team's per-test event list (Tests 2, 3 and 5) is what says which collections that is — the PRD's own
   * Epic 7 table only lists it for Test 5, but that table is a worked example, not the full requirement.
   *
   * Fires once each at 10%, 20%, 30% and 40% of the full page's scroll height (not the product grid
   * specifically). rAF-throttled so a fast scroll cannot fire the check more than once per frame.
   *
   * 40 IS OURS, NOT THE PRD'S. PRD 3.2 specifies 10/20/30; 40 was added on request to give the shallow end of
   * the funnel one more step before GA4's own automatic `scroll` event, which fires only at 90% and so leaves
   * everything between 30 and 90 unmeasured. It needs its own GrowthBook metric filtered on `percent = 40` — an
   * existing 10/20/30 metric will not pick it up.
   * ------------------------------------------------------------------------------------------------------------- */

  var SCROLL_DEPTH_COLLECTIONS = ['shoulder-bags','tote-bags', 'women-laptop-bags','bestsellers','work-essentials'];
  var SCROLL_THRESHOLDS = [10, 20, 30, 40];

  function initScrollDepth() {
    if (!collectionIn(SCROLL_DEPTH_COLLECTIONS)) return;

    var fired = {};
    var ticking = false;

    function check() {
      ticking = false;

      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - doc.clientHeight;
      if (scrollable <= 0) return;

      var percentScrolled = (window.scrollY / scrollable) * 100;

      SCROLL_THRESHOLDS.forEach(function (threshold) {
        if (fired[threshold] || percentScrolled < threshold) return;
        fired[threshold] = true;
        send('scroll_depth', { percent: threshold });
      });
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(check);
      },
      { passive: true }
    );

    // Covers a page that loads already scrolled (back-forward cache restore, deep link with a hash).
    check();
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * image_swipe_count / quick_view_image_swipe_count  —  card: women-laptop-bags, bestsellers, work-essentials
   *                                                     quick_view: women-laptop-bags, bestsellers
   * ---------------------------------------------------------------------------------------------------------------
   * TWO EVENT NAMES, DELIBERATELY AGAINST PRD 2.1/2.4, WHICH SPECIFIES ONE `image_swipe` EVENT WITH A `context`
   * PARAM. Changed on 2026-09-03 to match the metrics that already exist in GrowthBook.
   *
   * The reason is the shape of those metrics, not a preference. They are Fact Table metrics over a shared "GA4
   * Events" fact table, and their whole definition is a SQL row filter — `WHERE (event_name = '…')`. GA4's
   * BigQuery export keeps event params inside a repeated `event_params` record, so `context` is not a column on
   * that fact table and a row filter cannot reach it. Splitting on context in GrowthBook would have meant editing
   * the shared fact table, which every other metric is built on. Splitting here costs one event name.
   *
   * `context` IS STILL SENT on both, unchanged. It is now redundant with the event name, and that is the point:
   * it keeps the QA console check honest and leaves the door open to going back to one event if the fact table
   * ever exposes params as columns.
   *
   * product-card-ab.js owns the carousel mechanics and dispatches `ab:carousel-swipe` whenever a swipe reaches a
   * new slide it has not shown before; this file only decides the context and whether the current page's test
   * wants it.
   * ------------------------------------------------------------------------------------------------------------- */

  var IMAGE_SWIPE_CARD_COLLECTIONS = ['women-laptop-bags', 'bestsellers', 'work-essentials'];
  var IMAGE_SWIPE_QUICK_VIEW_COLLECTIONS = ['women-laptop-bags', 'bestsellers'];

  document.addEventListener('ab:carousel-swipe', function (event) {
    var detail = event.detail || {};
    var root = detail.root;
    if (!root) return;

    var context = root.closest('[data-quick-view-target], .quick-view__gallery') ? 'quick_view' : 'card';
    var allowed = context === 'quick_view' ? IMAGE_SWIPE_QUICK_VIEW_COLLECTIONS : IMAGE_SWIPE_CARD_COLLECTIONS;

    if (!collectionIn(allowed)) return;

    send(context === 'quick_view' ? 'quick_view_image_swipe_count' : 'image_swipe_count', {
      product_id: productIdOf(root),
      image_index: detail.index,
      context: context
    });
  });

  /* ---------------------------------------------------------------------------------------------------------------
   * quick_view_click  —  women-laptop-bags, bestsellers
   * ---------------------------------------------------------------------------------------------------------------
   * NAMED FOR THE GROWTHBOOK METRIC, not for the PRD's `quick_view_open` — same reasoning as the swipe events
   * above. The internal DOM event is still `ab:quick-view-open`; only the GA4 event name changed.
   *
   * product-card-ab.js dispatches `ab:quick-view-open` from openQuickView(), once per genuine trigger click (not
   * on the drawer's own in-place colour swap, which reuses the same panel rather than opening it).
   *
   * `source` ('card' | 'grid-breaker', PRD 2.2) is derived from the trigger's position in the grid — no test
   * currently puts a quick-view trigger on a grid-breaker tile, so this reads 'card' today but does not hardcode
   * that assumption.
   * ------------------------------------------------------------------------------------------------------------- */

  var QUICK_VIEW_OPEN_COLLECTIONS = ['women-laptop-bags', 'bestsellers'];

  document.addEventListener('ab:quick-view-open', function (event) {
    if (!collectionIn(QUICK_VIEW_OPEN_COLLECTIONS)) return;

    var trigger = event.detail && event.detail.trigger;
    if (!trigger) return;

    send('quick_view_click', {
      product_id: productIdOf(trigger),
      source: sourceOf(trigger)
    });
  });

  /* ---------------------------------------------------------------------------------------------------------------
   * component_visibility  —  work-essentials only
   * ---------------------------------------------------------------------------------------------------------------
   * PRD 2.1/2.5: fires once per Grid Breaker tile per page view (IntersectionObserver, disconnected after the
   * first time each tile crosses into view) — a reach metric, not a raw impression count, so scrolling past the
   * same tile twice does not double it.
   * ------------------------------------------------------------------------------------------------------------- */

  var COMPONENT_VISIBILITY_COLLECTIONS = ['work-essentials'];

  function initComponentVisibility() {
    if (!collectionIn(COMPONENT_VISIBILITY_COLLECTIONS)) return;
    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          send('component_viewability_rate', {
            component_type: 'grid_breaker_tile',
            product_id: productIdOf(entry.target),
            position: entry.target.dataset.position || ''
          });
        });
      },
      { threshold: 0.5 }
    );

    function observeBreakers() {
      document.querySelectorAll('.product-card--breaker').forEach(function (tile) {
        if (tile.dataset.viewabilityObserved) return;
        tile.dataset.viewabilityObserved = '1';
        io.observe(tile);
      });
    }

    observeBreakers();

    // product-card-ab.js is the script that actually adds .product-card--breaker — and it loads later in the
    // document (deferred, near the end of <body>) than this file (deferred, in <head>). Deferred scripts run
    // in document order, so on a first load this file's DOMContentLoaded work always runs BEFORE
    // markGridBreakers() has marked anything, and listening for theme:section:updated/shopify:section:load
    // does not fix that ordering either — both files bind those same events, and this one still registered its
    // listener first. Watching the DOM directly is the only trigger that is not sensitive to which script
    // happened to load first.
    if ('MutationObserver' in window) {
      var scanQueued = false;
      var breakerObserver = new MutationObserver(function () {
        if (scanQueued) return;
        scanQueued = true;
        requestAnimationFrame(function () {
          scanQueued = false;
          observeBreakers();
        });
      });
      breakerObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true
      });
    }

    // Belt and braces for pagination/filtering, which replaces cards outright rather than just toggling a class.
    document.addEventListener('theme:section:updated', observeBreakers);
    document.addEventListener('shopify:section:load', observeBreakers);
  }

  /* ---------------------------------------------------------------------------------------------------------------
   * grid_breaker_view_item  —  work-essentials only
   * ---------------------------------------------------------------------------------------------------------------
   * Not one of the PRD's Epic 2 events (its Epic 3 architecture prefers scoping the existing view_item event over
   * adding a new one), but explicitly requested by name for Test 5 in the team's per-test event list. Fires on a
   * click-through from a breaker tile specifically, mirroring PLP → PDP View Item but scoped to breaker tiles so
   * their click-through rate can be compared against standard tiles.
   * ------------------------------------------------------------------------------------------------------------- */

  var GRID_BREAKER_VIEW_ITEM_COLLECTIONS = ['work-essentials'];

  function initGridBreakerViewItem() {
    if (!collectionIn(GRID_BREAKER_VIEW_ITEM_COLLECTIONS)) return;

    document.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        var link = target.closest(
          '.product-card--breaker a.product-card__media, .product-card--breaker a.product-title'
        );
        if (!link) return;

        send('grid_breaker_view_item', { product_id: productIdOf(link) });
      },
      true
    );
  }

  initScrollDepth();
  initComponentVisibility();
  initGridBreakerViewItem();
})();
